/**
 * @module features/legal/LegalLayout
 * @description Standardized, responsive document wrapper for Terms of Service and Privacy Policy.
 * Features:
 * - Brand emblem and navigation header with back button and theme toggle.
 * - Clean document container with refined typography and readable 68ch line-length.
 * - Dynamic copyright footer referencing Roborosx Omni Tech Solutions LLP.
 */

import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Moon, Sun, ShieldCheck, FileText } from "lucide-react";
import { APP_NAME, ORG_NAME } from "@/config/env";

interface LegalLayoutProps {
  title: string;
  subtitle: string;
  lastUpdated: string;
  badge: "Terms of Service" | "Privacy Policy";
  children: React.ReactNode;
}

export function LegalLayout({
  title,
  subtitle,
  lastUpdated,
  badge,
  children,
}: LegalLayoutProps) {
  const navigate = useNavigate();

  const [isDark, setIsDark] = React.useState(() => {
    return document.documentElement.classList.contains("dark");
  });

  const toggleTheme = React.useCallback(() => {
    const nextIsDark = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", nextIsDark);
    localStorage.setItem("theme", nextIsDark ? "dark" : "light");
    setIsDark(nextIsDark);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground antialiased selection:bg-zinc-200 dark:selection:bg-zinc-800">
      {/* Top Header */}
      <header className="sticky top-0 z-30 w-full border-b border-border/80 bg-background/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          {/* Brand Logo & Back Action */}
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-border/80 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Go back"
              title="Go back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <Link
              to="/"
              className="flex items-center gap-2 font-semibold text-sm sm:text-base tracking-tight hover:opacity-90 transition-opacity"
            >
              <div className="h-6 w-6 rounded-full border-[2.5px] border-foreground flex items-center justify-center shrink-0">
                <div className="h-2 w-2 rounded-full bg-foreground" />
              </div>
              <span className="truncate">
                {APP_NAME}
                <span className="text-muted-foreground font-normal text-xs ml-2 hidden md:inline">
                  by {ORG_NAME}
                </span>
              </span>
            </Link>
          </div>

          {/* Quick links & Theme Switcher */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <nav className="hidden sm:flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Link
                to="/terms"
                className="px-2.5 py-1.5 rounded-md hover:text-foreground hover:bg-muted transition-colors"
              >
                Terms
              </Link>
              <Link
                to="/privacy"
                className="px-2.5 py-1.5 rounded-md hover:text-foreground hover:bg-muted transition-colors"
              >
                Privacy
              </Link>
            </nav>

            <div className="h-4 w-px bg-border hidden sm:block" />

            <button
              type="button"
              onClick={toggleTheme}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/80 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? (
                <Sun className="h-4 w-4 text-amber-400" />
              ) : (
                <Moon className="h-4 w-4 text-zinc-600" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Document Content */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Document Header */}
        <div className="mb-8 sm:mb-12 border-b border-border/80 pb-6 sm:pb-8">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-foreground mb-3">
            {badge === "Privacy Policy" ? (
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <FileText className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            )}
            <span>{badge}</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-foreground font-sans">
            {title}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl">
            {subtitle}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground font-mono">
            <span>Last Updated: {lastUpdated}</span>
            <span>·</span>
            <span>Organization: {ORG_NAME}</span>
          </div>
        </div>

        {/* Legal Body */}
        <article className="prose dark:prose-invert max-w-none text-sm sm:text-base leading-relaxed space-y-8 text-foreground/90">
          {children}
        </article>


      </main>

      {/* Footer */}
      <footer className="w-full border-t border-border/80 bg-muted/20 py-6 mt-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>
            &copy; {new Date().getFullYear()} {ORG_NAME}. All rights reserved.
          </p>
          <p className="font-mono text-[11px]">
            {APP_NAME} Enterprise IoT Device Gateway
          </p>
        </div>
      </footer>
    </div>
  );
}
